// VIP Trigger Service
// Token-based shared link for VIP song triggering

import { db, functions } from './firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { generateVerificationToken } from './events';
import { pickLatestSession } from '../utils/shareLinks';

// ============================================
// TYPES
// ============================================

export interface VipSession {
  id: string;
  eventId: string;
  djId: string;
  token: string;
  status: 'active' | 'revoked';
  createdAt: string;
}

const COLLECTION = 'vipSessions';

// ============================================
// CRUD OPERATIONS
// ============================================

export async function createVipSession(
  eventId: string,
  djId: string
): Promise<{ sessionId: string; token: string }> {
  const token = generateVerificationToken();
  const docRef = await addDoc(collection(db, COLLECTION), {
    eventId,
    djId,
    token,
    status: 'active',
    createdAt: new Date().toISOString(),
  });
  return { sessionId: docRef.id, token };
}

export async function getVipSession(
  eventId: string,
  djId: string
): Promise<VipSession | null> {
  const q = query(
    collection(db, COLLECTION),
    where('eventId', '==', eventId),
    where('djId', '==', djId)
  );
  const snap = await getDocs(q);
  const sessions = snap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }) as VipSession);
  return pickLatestSession(sessions, true);
}

export async function validateVipLink(eventId: string, token: string): Promise<string> {
  const callable = httpsCallable<{ kind: 'vip'; eventId: string; token: string }, { sessionId: string }>(
    functions,
    'validateShareLink'
  );
  const result = await callable({ kind: 'vip', eventId, token });
  return result.data.sessionId;
}

export interface VipTriggerParams {
  eventId: string;
  token: string;
  requestId: string;
}

export async function triggerVipSong(params: VipTriggerParams): Promise<void> {
  const callable = httpsCallable<VipTriggerParams, { ok: boolean }>(functions, 'triggerVipSong');
  await callable(params);
}

export async function deleteVipSession(sessionId: string): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await deleteDoc(ref);
}

export async function revokeVipSession(sessionId: string): Promise<void> {
  const ref = doc(db, COLLECTION, sessionId);
  await updateDoc(ref, { status: 'revoked' });
}

// ============================================
// HELPERS
// ============================================

export function generateVipQrUrl(eventId: string, token: string): string {
  const vipUrl = `${window.location.origin}/events/${eventId}/vip?token=${token}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(vipUrl)}`;
}

export function getVipPageUrl(eventId: string, token: string): string {
  return `${window.location.origin}/events/${eventId}/vip?token=${token}`;
}
